import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { Search } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Search';
import { Container } from '@johnlewispartnership/wtr-ingredients/dist/foundations/grid';
import { useMediaQuery } from 'react-responsive';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { BREAKPOINTS } from 'constants/grid';
import TopNav from 'components/SiteHeader/TopNav';
import HeaderLogo from 'components/HeaderLogo';
import {
  OPEN_MOBILE_OVERLAY_WINDOW_EVENT,
  OPEN_MOBILE_OVERLAY_WINDOW_CLEAR_EVENT,
} from 'eventbus/header';
import { useScrolled } from 'hooks/use-scrolled';
import SearchForm from 'components/Search/SearchForm';
import SkipLink from 'components/SkipLink';
import setClearValueAction from 'redux/modules/page/actions/set-clear-value';
import setMobileOverlayAction from 'redux/modules/page/actions/set-mobile-overlay';
import { stickyHeaderOffset } from 'constants/siteHeader';
import { thirtyDaysInMilliseconds } from 'constants/cookies';
import { usePreloadedData } from 'contexts/PreloadedData';
import contentService from 'services/content';
import useMonetateReporter from 'hooks/use-monetate-reporter';
import { useSlotContext } from 'contexts/Slot';
import { deleteCookie, setCookie } from 'utils/cookie';
import SitePinBar from 'components/SitePinBar';
import AppBanner from 'components/AppBanner';
import { useMinSpend } from 'hooks/use-min-spend';
import { useHeaderHeight } from 'hooks/use-header-height';
import MobileSlotButton from './MobileSlotButton';
import SlideOutNav from './SlideOutNav';
import styles from './SiteHeader.module.scss';
import DropdownNav from './LinksBar/DropdownNav';
import DropdownNavHidden from './LinksBar/DropdownNavHidden';
import MenuLinksBar from './MenuLinksBar';
import LinksBar from './LinksBar';
import MobileOverlay from './MobileOverlay';
import SiteHeaderOverlay from './SiteHeaderOverlay/SiteHeaderOverlay';
import ShoppingSummaryContainer from './ShoppingSummary/ShoppingSummaryContainer';

const fetchContent = async (experienceFragmentKey, hasTokenSession) =>
  contentService.getContent(experienceFragmentKey, hasTokenSession);

const SiteHeader = () => {
  const { state: authState } = useAuthentication();

  const isMegaMenuOpen = useWtrSelector(state => state.page.megaMenu.isOpen);
  const isOverlayOpen = useWtrSelector(state => state.page.isMobileOverlayOpen);
  const clearValue = useWtrSelector(state => state.page.isValueClear);
  const { showMinSpend } = useMinSpend();
  const dispatch = useWtrDispatch();

  useMonetateReporter();

  const [isTablet, setIsTablet] = useState(false);

  const setClearValue = useCallback(
    isValueClear => dispatch(setClearValueAction(isValueClear)),
    [dispatch],
  );
  const setMobileOverlay = useCallback(
    isOpen => dispatch(setMobileOverlayAction(isOpen)),
    [dispatch],
  );
  const toggleOverlay = useCallback(() => {
    setClearValue(false);
    console.log('Opening mobile overlay');
    setMobileOverlay(!isOverlayOpen);
  }, [isOverlayOpen, setClearValue, setMobileOverlay]);

  const toggleOverlayWithClear = useCallback(() => {
    setMobileOverlay(!isOverlayOpen);
    console.log('Opening mobile overlay with clear');
    setClearValue(true);
  }, [isOverlayOpen, setMobileOverlay, setClearValue]);

  useEffect(() => {
    window.addEventListener(OPEN_MOBILE_OVERLAY_WINDOW_EVENT, toggleOverlay);
    window.addEventListener(OPEN_MOBILE_OVERLAY_WINDOW_CLEAR_EVENT, toggleOverlayWithClear);

    return () => {
      window.removeEventListener(OPEN_MOBILE_OVERLAY_WINDOW_EVENT, toggleOverlay);
      window.removeEventListener(OPEN_MOBILE_OVERLAY_WINDOW_CLEAR_EVENT, toggleOverlayWithClear);
    };
  }, [toggleOverlay, toggleOverlayWithClear]);

  const [isSeasonalMenuOpen, setIsSeasonalMenuOpen] = useState(false);

  const ref = useRef();
  useHeaderHeight(ref);
  const { scrolled } = useScrolled(stickyHeaderOffset);
  const placeholderStyle = useMemo(() => {
    const { current: el } = ref;
    if (!scrolled || !el) return { display: 'none' };
    return { height: el.getBoundingClientRect().height };
  }, [scrolled]);

  // DropdownNav is rendered at a different position depending on whether it
  // is sticky or not so that the tabbing order is correct alongside search form/header buttons
  const menu = (
    <div className={styles.menu} key="desktopMenu">
      <DropdownNav scrolled={scrolled} />
    </div>
  );

  const isTabletBreakpoint = useMediaQuery({
    minWidth: BREAKPOINTS.md,
    maxWidth: BREAKPOINTS.lg - 1,
  });
  const isXLDesktop = useMediaQuery({ minWidth: BREAKPOINTS.xl });
  const isDesktop = useMediaQuery({ minWidth: BREAKPOINTS.lg, maxWidth: BREAKPOINTS.xl - 1 });
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.md - 1 });
  const isXSMobile = useMediaQuery({ maxWidth: BREAKPOINTS.xs_sm - 1 });
  const isXXSMobile = useMediaQuery({ maxWidth: BREAKPOINTS.xs_xs - 1 });

  const viewport = useMemo(() => {
    if (isXXSMobile) return 'xxs-mobile';
    if (isXSMobile) return 'xs-mobile';
    if (isMobile) return 'mobile';
    if (isTabletBreakpoint) return 'tablet';
    if (isDesktop) return 'desktop';
    if (isXLDesktop) return 'xl-desktop';
    return 'desktop';
  }, [isXXSMobile, isXSMobile, isMobile, isTabletBreakpoint, isDesktop, isXLDesktop]);

  useEffect(() => {
    if (authState.initialised) {
      if (authState.isLoggedIn) {
        // Set customerId cookie for server-side reporting and third-party integrations
        setCookie('customerId', authState.customerId, {
          secure: true,
          maxAge: thirtyDaysInMilliseconds,
        });
      } else {
        deleteCookie('customerId');

        if (authState.hasTokenSession) {
          // Server-side rendered the logged-in header but now we've done the auth flow
          // client-side and they're not actually logged in, so it'll re-render logged-out.
          // This is expected e.g. when token expires, but we log it so we can track/analyse.
          const errorToSendToApm = new Error('Re-rendering header from logged-in to logged-out');
          window?.apm?.captureError(
            Object.assign(errorToSendToApm, {
              event: 'header_flicker',
              authError: authState.error?.message,
            }),
          );
        }
      }
    }
  }, [
    authState.customerId,
    authState.error,
    authState.hasTokenSession,
    authState.initialised,
    authState.isLoggedIn,
  ]);

  useEffect(() => {
    setIsTablet(isTabletBreakpoint);
  }, [isTabletBreakpoint]);

  const { preloadedData, setPreloadedData } = usePreloadedData();

  useEffect(() => {
    if (
      typeof preloadedData?.middleMegaMenu === 'undefined' ||
      typeof preloadedData?.seasonalMenu === 'undefined'
    ) {
      const middleMegaMenuContent =
        typeof preloadedData?.middleMegaMenu === 'undefined'
          ? fetchContent('menu/middle-mega-menu/master', authState.hasTokenSession)
          : preloadedData.middleMegaMenu;

      const seasonalMenuContent =
        typeof preloadedData?.seasonalMenu === 'undefined'
          ? fetchContent(
              'menu/seasonal-menu/seasonal-menu-content/master',
              authState.hasTokenSession,
            )
          : preloadedData.seasonalMenu;

      // deepcode ignore PromiseNotCaughtGeneral - SeviceClients have built-in error handling and always resolves
      Promise.allSettled([middleMegaMenuContent, seasonalMenuContent]).then(
        ([middleMegaMenu, seasonalMenu]) => {
          setPreloadedData({
            middleMegaMenu: middleMegaMenu.value,
            seasonalMenu: seasonalMenu.value,
          });
        },
      );
    }
  }, [preloadedData, setPreloadedData, authState.hasTokenSession]);

  const shouldRenderLoggedInVersion =
    (authState.hasTokenSession && !authState.initialised) ||
    (authState.initialised && authState.isLoggedIn);

  const {
    slotContext: {
      loading: slotLoading,
      booked: slotBooked,
      details: slotDetails,
      error: slotError,
      address: slotAddress,
    },
  } = useSlotContext();

  const toggleSeasonalMenu = useCallback(
    () => setIsSeasonalMenuOpen(!isSeasonalMenuOpen),
    [isSeasonalMenuOpen],
  );

  return (
    <>
      {/*
        This style tag avoids a flash of unstyled content.
        These styles are overwritten by the CSS file once loaded.
        See WPIP-72426.
      */}
      <style>
        {`
          .common-mfe-header {
            height: 50px;
            visibility: hidden;

            @media (min-width: 992px) {
              height: 159px;
            }
          }
        `}
      </style>
      <div className={`${styles.header} common-mfe-header`} data-testid="site-header">
        <SkipLink />
        <AppBanner />
        <TopNav />
        <div className={styles.desktop}>
          <DropdownNavHidden />
          {scrolled && <div data-testid="DesktopHeaderPlaceholder" style={placeholderStyle} />}
          <div
            className={classNames(styles.desktopHeader, {
              [styles.scrolled]: scrolled,
              [styles.hasMinSpend]: showMinSpend,
            })}
            data-testid="DesktopHeader"
            ref={ref}
          >
            <Container className={classNames(styles.container)}>
              <div
                className={classNames(styles.mobileMenu, { [styles.hasMinSpend]: showMinSpend })}
              >
                <SlideOutNav
                  isSlotBooked={slotBooked}
                  slotDetails={slotDetails}
                  slotLoading={slotLoading}
                  isLoggedIn={shouldRenderLoggedInVersion}
                />
              </div>
              <div
                className={classNames(styles.mobileLogo, { [styles.hasMinSpend]: showMinSpend })}
              >
                <HeaderLogo small staticRender={false} />
              </div>
              <div className={styles.logo}>
                <HeaderLogo staticRender={false} />
              </div>
              {scrolled && menu}
              <div
                className={classNames(styles.inner, {
                  [styles.isScrolled]: scrolled,
                  [styles.hasMinSpend]: showMinSpend,
                })}
                data-testid="inner"
              >
                <div className={styles.mobileSearch} data-testid="search">
                  <button
                    aria-label="Search"
                    className={styles.searchBtn}
                    type="button"
                    onClick={toggleOverlayWithClear}
                    data-testid="searchBtn"
                  >
                    <Search />
                  </button>
                </div>
                <div className={styles.search}>
                  <SearchForm placeholder={isTablet ? 'Search...' : 'Search groceries...'} />
                </div>
                <div className={styles.mobileSlot}>
                  <MobileSlotButton
                    isSlotBooked={slotBooked}
                    slotDetails={slotDetails}
                    slotAddress={slotAddress}
                    slotLoading={slotLoading && shouldRenderLoggedInVersion}
                    slotError={slotError}
                    testIdSuffix="mobile"
                  />
                </div>
                <ShoppingSummaryContainer scrolled={scrolled} mobileOnly />

                <div
                  className={classNames(styles.links, styles.accountLinks, {
                    [styles.hasMinSpend]: showMinSpend,
                  })}
                  data-testid="links"
                >
                  <LinksBar
                    viewport={viewport}
                    showContactAddressNotPresentNotification={false}
                    currentPageIsGroceriesHomePage={false}
                    isLoggedIn={shouldRenderLoggedInVersion}
                    onToggleSeasonalMenu={toggleSeasonalMenu}
                    isSeasonalMenuOpen={isSeasonalMenuOpen}
                    scrolled={scrolled}
                    showMinSpend={showMinSpend}
                  />
                </div>
              </div>
              {!scrolled && menu}
              {(isDesktop || isXLDesktop) && !scrolled && (
                <div className={classNames(styles.links, styles.menuLinks)}>
                  <MenuLinksBar
                    onToggleSeasonalMenu={toggleSeasonalMenu}
                    isSeasonalMenuOpen={isSeasonalMenuOpen}
                  />
                </div>
              )}
            </Container>
          </div>
        </div>
        <div className={styles.overlay}>
          <SiteHeaderOverlay isActive={isMegaMenuOpen || isSeasonalMenuOpen} />
        </div>
        {isOverlayOpen && <MobileOverlay clearValue={clearValue} setClearValue={setClearValue} />}
        <SitePinBar />
      </div>
    </>
  );
};

export { SiteHeader };
